'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useGlobal } from '@/contexts/GlobalContext';
import { useToast } from '@/contexts/ToastContext';
import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import ConfirmModalContent from '@/components/layouts/ConfirmModalContent';
import Button from '@/components/common/Button';
import { SupportStatus } from '@/types/supporters';
import { requestSupporting, approveSupporting, cancelSupporting } from '@/repositories/supporters';
import { mutateWithoutLoading } from '@/hooks/swr/useSWRWithLoadingControl';

interface SupportingButtonProps {
  propsFootballerId: number;
  propsSupportStatus?: SupportStatus;
}

/**
 * サポートボタンコンポーネント
 * @param param0
 * @returns
 */

const SupportingButton: React.FC<SupportingButtonProps> = ({ propsFootballerId, propsSupportStatus }) => {
  /**
   * React context
   */
  const context = useGlobal();
  const { showSuccessMessage, showErrorMessage } = useToast();
  const { openConfirmModal, closeConfirmModal } = useConfirmModal();

  /**
   * Next router
   */
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * useState
   */
  const [isLoading, setIsLoading] = useState(false);
  const [isOKButtonLoading, setIsOKButtonLoading] = useState(false);
  const [isNGButtonLoading, setIsNGButtonLoading] = useState(false);
  const [supportingButton, setSupportingButton] = useState<React.ReactNode>(null);

  /**
   * サポートリクエスト
   */
  const handleRequestSupporting = async (footballerId: number) => {
    try {
      // ボタンローディング表示
      setIsLoading(true);

      // サポートリクエスト
      const resSupport = await requestSupporting(footballerId, context.csrftoken || '');

      // サポートレコード整合性の考慮
      if (!resSupport.ok) {
        throw new Error('support redcords integrity error');
      }

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後の画面遷移で、以前の状態がキャッシュされたまま画面表示されてしまうのを防止する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      showSuccessMessage('サポートリクエストしました。');

      // サポートリクエスト後の状態を設定
      // setSupportStatus('request');
    } catch {
      // トースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      // ボタンローディング非表示
      setIsLoading(false);
    }
  };

  /**
   * サポートリクエスト承認
   */
  const handleApproveSupporting = async (footballerId: number) => {
    try {
      // ボタンローディング表示
      setIsOKButtonLoading(true);

      // サポートリクエスト承認
      const resSupport = await approveSupporting(footballerId, context.csrftoken || '');
      // サポートレコード整合性の考慮
      if (!resSupport.ok) {
        throw new Error('support redcords integrity error');
      }

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後の画面遷移で、以前の状態がキャッシュされたまま画面表示されてしまうのを防止する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      showSuccessMessage('リクエストを承認しました。');

      // サポート承認後の状態を設定
      // setSupportStatus('supporter');
    } catch {
      // トースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      // ボタンローディング非表示
      setIsOKButtonLoading(false);
    }
  };

  /**
   * サポートリクエスト取り消し
   */
  const handleCancelRequest = async (footballerId: number) => {
    try {
      // ボタンローディング表示
      setIsNGButtonLoading(true);

      // サポートリクエスト解除
      const resSupport = await cancelSupporting(footballerId, context.csrftoken || '');
      // サポートレコード整合性の考慮
      if (!resSupport.ok) {
        throw new Error('support redcords integrity error');
      }

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後の画面遷移で、以前の状態がキャッシュされたまま画面表示されてしまうのを防止する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      showSuccessMessage('リクエストを取消しました。');
    } catch {
      // トースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      // ボタンローディング非表示
      setIsNGButtonLoading(false);
    }
  };

  /**
   * サポート解除
   */
  const handleCancelSupporting = async (footballerId: number) => {
    try {
      // ボタンローディング表示
      setIsNGButtonLoading(true);

      // サポートリクエスト解除
      const resSupport = await cancelSupporting(footballerId, context.csrftoken || '');
      // サポートレコード整合性の考慮
      if (!resSupport.ok) {
        throw new Error('support redcords integrity error');
      }

      // 1秒の遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後の画面遷移で、以前の状態がキャッシュされたまま画面表示されてしまうのを防止する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      showSuccessMessage('サポートを解除しました。');
    } catch {
      // トースト表示
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      // ボタンローディング非表示
      setIsNGButtonLoading(false);
    }
  };

  /**
   * サポートリクエスト解除OK時の処理
   */
  const handleConfirmCancelSupportingOK = async () => {
    // 確認モーダル閉じる
    closeConfirmModal();
    await handleCancelSupporting(propsFootballerId);
  };

  /**
   * サポートリクエスト解除確認モーダル表示
   */
  const handleConfirmCancelSupporting = () => {
    // 確認モーダル表示
    openConfirmModal(
      <ConfirmModalContent onClickOKCallback={handleConfirmCancelSupportingOK}>
        サポートを解除します。
        <br />
        よろしいですか？
      </ConfirmModalContent>
    );
  };

  useEffect(() => {
    let buttonNode: React.ReactNode;
    switch (propsSupportStatus) {
      case 'none':
        buttonNode = (
          <Button
            type="button"
            buttonType="primary"
            className="text-xs"
            isLoading={isLoading}
            loadingMessage="サポートリクエスト"
            onClick={() => handleRequestSupporting(propsFootballerId)}>
            <svg
              className="w-4 h-4 me-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 8h6m-3 3V5m-6-.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
            サポートリクエスト
          </Button>
        );
        break;
      case 'request':
        buttonNode = (
          <Button
            type="button"
            buttonType="outlineWhite"
            className="text-xs"
            isLoading={isNGButtonLoading}
            loadingMessage="リクエスト中"
            onClick={() => handleCancelRequest(propsFootballerId)}>
            <svg
              className="w-4 h-4 me-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 3a3 3 0 1 1-1.614 5.53M15 12a4 4 0 0 1 4 4v1h-3.348M10 4.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
            リクエスト中
          </Button>
        );
        break;
      case 'requested':
        buttonNode = (
          <div className="flex justify-start">
            <Button
              type="button"
              buttonType="primary"
              className="text-xs me-3 min-w-fit"
              isLoading={isOKButtonLoading}
              loadingMessage="OK"
              onClick={() => handleApproveSupporting(propsFootballerId)}>
              <svg
                className="w-4 h-4 me-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 18">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 8h6m-3 3V5m-6-.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
                />
              </svg>
              OK
            </Button>
            <Button
              type="button"
              buttonType="outlineCancel"
              className="text-xs min-w-fit"
              isLoading={isNGButtonLoading}
              loadingMessage="NG"
              onClick={() => handleCancelSupporting(propsFootballerId)}>
              <svg
                className="w-4 h-4 me-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 18">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 8h6m-9-3.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
                />
              </svg>
              NG
            </Button>
          </div>
        );
        break;
      case 'supporter':
        buttonNode = (
          <Button
            type="button"
            className="text-xs"
            buttonType="outlineWhite"
            isLoading={isNGButtonLoading}
            loadingMessage="サポート中"
            onClick={handleConfirmCancelSupporting}>
            <svg
              className="w-4 h-4 me-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 18">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 3a3 3 0 1 1-1.614 5.53M15 12a4 4 0 0 1 4 4v1h-3.348M10 4.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5 11h3a4 4 0 0 1 4 4v2H1v-2a4 4 0 0 1 4-4Z"
              />
            </svg>
            サポート中
          </Button>
        );
        break;
      default:
        buttonNode = <></>;
        break;
    }
    setSupportingButton(buttonNode);
  }, [propsSupportStatus, isLoading, isOKButtonLoading, isNGButtonLoading, propsFootballerId]);

  return (
    <>
      <div>{supportingButton}</div>
    </>
  );
};

export default SupportingButton;
